import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/dashboard/admin-sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/theme-toggle";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Search, Plus, Mail, Phone, X, Loader2, CheckCircle, Trash2, ChevronDown, ChevronUp, Edit, MoreVertical, Eye, FileText, History, Upload, Send, Play } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, API_BASE_URL } from "@/lib/queryClient";
import { formatPlatformCurrency, formatPlatformCurrencyExact } from "@/lib/currency";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const investorSchema = z.object({
  firstName: z.string().min(2, "First name is required"),
  lastName: z.string().min(2, "Last name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().optional(),
  panNumber: z.string().optional(),
  clientId: z.string().optional(),
  investmentAmount: z.string().optional(),
  investorType: z.string().optional(),
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(1, "Confirm password is required"),
}).refine((data) => {
  if (data.password !== data.confirmPassword) {
    return false;
  }
  return true;
}, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type InvestorFormData = z.infer<typeof investorSchema>;

interface InvestorWithPortfolio {
  id: string;
  _id?: string;
  firstName: string | null;
  lastName: string | null;
  clientId: string | null;
  email: string | null;
  phone: string | null;
  kycStatus: string;
  createdAt: string | null;
  portfolio?: {
    _id?: string;
    id?: string;
    fundName?: string;
    totalInvested?: string;
    currentValue?: string;
    returns?: string;
    irr?: string;
    privateCreditAllocation?: string;
    aifExposure?: string;
    cashEquivalents?: string;
    currencyCommodities?: string;
    maturity?: string;
    deploymentStatus?: string;
    allocationDate?: string;
  } | null;
}

function getInvestorId(inv: { id?: string; _id?: string } | null | undefined): string {
  return String(inv?.id || inv?._id || "").trim();
}

export default function AdminInvestorsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState<{ email: string; tempPassword: string } | null>(null);
  const [expandedInvestorId, setExpandedInvestorId] = useState<string | null>(null);

  // New state for action dialogs
  const [selectedInvestor, setSelectedInvestor] = useState<InvestorWithPortfolio | null>(null);
  const [investorToDelete, setInvestorToDelete] = useState<InvestorWithPortfolio | null>(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showTransactionsDialog, setShowTransactionsDialog] = useState(false);
  const [showGenerateStatementDialog, setShowGenerateStatementDialog] = useState(false);
  const [showAgreementsDialog, setShowAgreementsDialog] = useState(false);
  const [agreementFile, setAgreementFile] = useState<File | null>(null);
  const [agreementTitle, setAgreementTitle] = useState("Contribution Agreement");
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [credentialsPreview, setCredentialsPreview] = useState<{ email: string; tempPassword: string } | null>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: backendInvestors } = useQuery<InvestorWithPortfolio[]>({
    queryKey: ["/api/admin/investors"],
  });

  const { data: allPortfolios } = useQuery<any[]>({
    queryKey: ["/api/admin/portfolios"],
    enabled: !!expandedInvestorId,
  });

  const { data: allWithdrawals } = useQuery<any[]>({
    queryKey: ["/api/admin/withdrawals"],
    enabled: !!expandedInvestorId,
  });

  // Fetch investor details when viewing
  const selectedInvestorId = getInvestorId(selectedInvestor);
  const { data: investorDetails, isLoading: loadingDetails } = useQuery({
    queryKey: ["/api/admin/investors", selectedInvestorId],
    queryFn: async () => {
      if (!selectedInvestorId) return null;
      const res = await fetch(`${API_BASE_URL}/api/admin/investors/${selectedInvestorId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch details");
      return res.json();
    },
    enabled: !!selectedInvestorId && (showViewDialog || showEditDialog),
  });

  // Fetch investor transactions
  const { data: investorTransactions, isLoading: loadingTransactions } = useQuery({
    queryKey: ["/api/admin/investors", selectedInvestorId, "transactions"],
    queryFn: async () => {
      if (!selectedInvestorId) return [];
      const res = await fetch(`${API_BASE_URL}/api/admin/transactions?investorId=${selectedInvestorId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch transactions");
      return res.json();
    },
    enabled: !!selectedInvestorId && showTransactionsDialog,
  });

  // Generate statement mutation
  const generateStatementMutation = useMutation({
    mutationFn: async (data: { investorId: string; type: string; period: string; year: number }) => {
      const response = await apiRequest("POST", "/api/admin/statements/generate", data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Statement Generated",
        description: "The statement has been generated successfully.",
      });
      setShowGenerateStatementDialog(false);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/statements"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to generate statement. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Fetch investor agreements
  const { data: investorAgreements, isLoading: loadingAgreements } = useQuery({
    queryKey: ["/api/admin/investors", selectedInvestorId, "agreements"],
    queryFn: async () => {
      if (!selectedInvestorId) return [];
      const res = await fetch(`${API_BASE_URL}/api/admin/investors/${selectedInvestorId}/agreements`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch agreements");
      return res.json();
    },
    enabled: !!selectedInvestorId && showAgreementsDialog,
  });

  const uploadAgreementMutation = useMutation({
    mutationFn: async () => {
      if (!agreementFile || !selectedInvestorId) throw new Error("File and Investor are required");
      const formData = new FormData();
      formData.append("file", agreementFile);
      formData.append("investorId", selectedInvestorId);
      formData.append("title", agreementTitle);

      const res = await fetch(`${API_BASE_URL}/api/admin/agreements/upload`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to upload agreement");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Agreement Uploaded", description: "PDF agreement saved successfully." });
      setAgreementFile(null);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/investors", selectedInvestorId, "agreements"] });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteAgreementMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/admin/agreements/${id}`);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Agreement deleted" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/investors", selectedInvestorId, "agreements"] });
    },
  });

  // Edit investor mutation
  const editInvestorMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      updates: Record<string, unknown>;
      portfolioId?: string;
      allocation?: {
        privateCreditAllocation: number;
        aifExposure: number;
        cashEquivalents: number;
        currencyCommodities?: number;
        fundName?: string;
        totalInvested?: string;
        currentValue?: string;
        returns?: string;
        irr?: string;
        maturity?: string;
        deploymentStatus?: string;
        allocationDate?: string;
      };
    }) => {
      const response = await apiRequest("PUT", `/api/admin/investors/${data.id}`, data.updates);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || "Failed to update investor profile");
      }

      if (data.portfolioId && data.allocation) {
        const allocationResponse = await apiRequest(
          "PUT",
          `/api/admin/portfolios/${data.portfolioId}/allocation`,
          data.allocation
        );
        if (!allocationResponse.ok) {
          const body = await allocationResponse.json().catch(() => ({}));
          throw new Error(body.message || "Failed to update portfolio allocation");
        }
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Profile Updated",
        description: "Investor profile and portfolio allocation have been saved.",
      });
      setShowEditDialog(false);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/investors"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/portfolios"] });
      if (selectedInvestorId) {
        queryClient.invalidateQueries({ queryKey: ["/api/admin/investors", selectedInvestorId] });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Delete investor mutation
  const deleteInvestorMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("DELETE", `/api/admin/investors/${id}`);
      if (!response.ok) {
        throw new Error("Failed to delete investor");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Investor Deleted",
        description: "The investor account has been successfully removed.",
      });
      setShowDeleteDialog(false);
      setInvestorToDelete(null);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/investors"] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete investor. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Action handlers — resolve against backend records using id or _id
  const resolveBackendInvestor = (investor: { id?: string }) => {
    const targetId = String(investor?.id || "").trim();
    if (!targetId || !backendInvestors) return null;
    const found = backendInvestors.find((i) => getInvestorId(i) === targetId);
    if (!found) return null;
    return { ...found, id: getInvestorId(found) };
  };

  const handleViewDetails = (investor: any) => {
    setSelectedInvestor(resolveBackendInvestor(investor));
    setShowViewDialog(true);
  };

  const handleEditProfile = (investor: any) => {
    setSelectedInvestor(resolveBackendInvestor(investor));
    setShowEditDialog(true);
  };

  const handleGenerateStatement = (investor: any) => {
    setSelectedInvestor(resolveBackendInvestor(investor));
    setShowGenerateStatementDialog(true);
  };

  const handleViewTransactions = (investor: any) => {
    setSelectedInvestor(resolveBackendInvestor(investor));
    setShowTransactionsDialog(true);
  };

  const handleDeleteWithConfirmation = (investor: any) => {
    setInvestorToDelete(resolveBackendInvestor(investor));
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    const id = getInvestorId(investorToDelete);
    if (id) {
      deleteInvestorMutation.mutate(id);
    }
  };

  const sendCredentialsMutation = useMutation({
    mutationFn: async (investorId: string) => {
      const response = await apiRequest("POST", `/api/admin/investors/${investorId}/send-credentials`, {});
      return response.json();
    },
    onSuccess: (data: { email: string; tempPassword: string }) => {
      setCredentialsPreview({ email: data.email, tempPassword: data.tempPassword });
      toast({
        title: "Credentials generated",
        description: "Share the temporary password with the investor.",
      });
    },
    onError: () => {
      toast({ title: "Failed to generate credentials", variant: "destructive" });
    },
  });

  const bulkUploadMutation = useMutation({
    mutationFn: async (investorsPayload: Record<string, string>[]) => {
      const response = await apiRequest("POST", "/api/admin/investors/bulk-upload", { investors: investorsPayload });
      return response.json();
    },
    onSuccess: (data: { created: number }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/investors"] });
      toast({
        title: "Bulk upload complete",
        description: `${data.created} investor(s) created.`,
      });
      setShowBulkDialog(false);
      setBulkFile(null);
    },
    onError: (error: Error) => {
      toast({ title: "Bulk upload failed", description: error.message, variant: "destructive" });
    },
  });

  const verifyTransactionMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("PUT", `/api/admin/transactions/${id}/verify`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/investors", selectedInvestor?.id, "transactions"] });
      toast({ title: "Transaction verified" });
    },
  });

  const processTransactionMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("PUT", `/api/admin/transactions/${id}/process`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/investors", selectedInvestor?.id, "transactions"] });
      toast({ title: "Transaction processed" });
    },
  });

  const parseBulkCsv = (text: string) => {
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) throw new Error("CSV must include a header row and at least one data row.");

    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const required = ["firstname", "lastname", "email", "phone", "pannumber", "investmentamount", "investortype"];
    for (const col of required) {
      if (!headers.includes(col)) {
        throw new Error(`Missing column: ${col}. Required: firstName, lastName, email, phone, panNumber, investmentAmount, investorType`);
      }
    }

    return lines.slice(1).map((line) => {
      const values = line.split(",").map((v) => v.trim());
      const row: Record<string, string> = {};
      headers.forEach((h, i) => {
        row[h] = values[i] || "";
      });
      return {
        firstName: row.firstname,
        lastName: row.lastname,
        email: row.email,
        phone: row.phone,
        panNumber: row.pannumber,
        investmentAmount: row.investmentamount,
        investorType: row.investortype,
      };
    });
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    const text = await bulkFile.text();
    const investorsPayload = parseBulkCsv(text);
    bulkUploadMutation.mutate(investorsPayload);
  };

  const investors = backendInvestors && Array.isArray(backendInvestors)
    ? backendInvestors
        .map((inv) => {
          const entityId = String((inv as any)?.id || (inv as any)?._id || "").trim();
          if (!entityId) return null;
          const p = inv?.portfolio;
          return {
            id: entityId,
            name: `${inv?.firstName || ""} ${inv?.lastName || ""}`.trim() || "Unknown",
            clientId: inv?.clientId || "—",
            email: inv?.email || "—",
            phone: inv?.phone || "—",
            product: p?.fundName || "Velocity Fund",
            investedAmountNum: p?.totalInvested ? parseFloat(p.totalInvested) : 0,
            currentValueNum: p?.currentValue ? parseFloat(p.currentValue) : 0,
            returns: p?.returns ? formatPlatformCurrencyExact(parseFloat(p.returns)) : "₹0",
            kycStatus: inv?.kycStatus || "pending",
            joinDate: inv?.createdAt ? new Date(inv.createdAt).toISOString().split("T")[0] : "—",
          };
        })
        .filter((inv): inv is NonNullable<typeof inv> => inv !== null)
    : [];

  const form = useForm<InvestorFormData>({
    resolver: zodResolver(investorSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      panNumber: "",
      investmentAmount: "",
      investorType: "",
      password: "",
      confirmPassword: "",
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: InvestorFormData) => {
      const response = await apiRequest("POST", "/api/admin/investors", data);
      return response.json();
    },
    onSuccess: (responseData) => {
      if (responseData.credentials) {
        setGeneratedCredentials({
          email: responseData.credentials.email,
          tempPassword: responseData.credentials.tempPassword,
        });
      }
      setOnboardingComplete(true);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/investors"] });
      toast({
        title: "Investor Onboarded",
        description: "New investor has been successfully added to the system.",
      });
    },
    onError: (error: Error) => {
      console.error("Create investor error:", error);
      toast({
        title: "Creation Failed",
        description: error.message || "Failed to add investor. Please checking your input.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: InvestorFormData) => {
    mutation.mutate(data);
  };

  const handleCloseDialog = () => {
    setShowAddDialog(false);
    setOnboardingComplete(false);
    setGeneratedCredentials(null);
    form.reset();
  };

  const filteredInvestors = investors.filter(
    (investor) =>
      investor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      investor.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sidebarStyle = {
    "--sidebar-width": "17rem",
    "--sidebar-width-icon": "3.5rem",
  };

  return (
    <SidebarProvider style={sidebarStyle as React.CSSProperties}>
      <div className="flex h-screen w-full overflow-hidden">
        <AdminSidebar />
        <div className="flex flex-col flex-1 h-full overflow-hidden">
          <header className="flex items-center justify-between gap-4 h-16 px-6 border-b bg-background/95 backdrop-blur shrink-0">
            <div className="flex items-center gap-4">
              <SidebarTrigger data-testid="admin-button-sidebar-toggle" />
              <div>
                <h1 className="text-lg font-semibold">Investors</h1>
                <p className="text-sm text-muted-foreground">Manage investor accounts</p>
              </div>
            </div>
            <ThemeToggle />
          </header>

          <main className="flex-1 p-6 space-y-6 overflow-y-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search investors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="admin-input-search-investors"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" className="gap-2" onClick={() => setShowBulkDialog(true)}>
                  <Upload className="w-4 h-4" />
                  Bulk Upload
                </Button>
                <Button className="gap-2" onClick={() => setShowAddDialog(true)} data-testid="admin-button-add-investor">
                  <Plus className="w-4 h-4" />
                  Add Investor
                </Button>
              </div>
            </div>

            <Card className="bg-card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">
                  All Investors ({filteredInvestors.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="w-10"></th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Investor</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Investor ID</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Contact</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Invested</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Current Value</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">KYC Status</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground text-right pr-10">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInvestors.map((investor) => (
                        <React.Fragment key={investor.id}>
                          <tr 
                            className={`border-b last:border-0 hover:bg-muted/30 transition-colors cursor-pointer ${expandedInvestorId === investor.id ? "bg-accent/5" : ""}`}
                            onClick={() => setExpandedInvestorId(expandedInvestorId === investor.id ? null : investor.id)}
                          >
                            <td className="py-4 px-2 text-center text-muted-foreground">
                              {expandedInvestorId === investor.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-3">
                                <Avatar className="h-9 w-9 border border-border">
                                  <AvatarFallback className="bg-accent/10 text-accent text-sm font-bold">
                                    {investor.name.split(" ").map(n => n[0]).join("")}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-semibold text-sm">{investor.name}</p>
                                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                                    Joined {new Date(investor.joinDate).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-4 text-sm font-medium text-muted-foreground">
                              {investor.clientId}
                            </td>
                            <td className="py-4 px-4">
                              <div className="space-y-0.5">
                                <div className="flex items-center gap-2 text-[13px]">
                                  <Mail className="w-3 h-3 text-muted-foreground" />
                                  {investor.email}
                                </div>
                                <div className="flex items-center gap-2 text-[11px] text-muted-foreground font-mono">
                                  <Phone className="w-3 h-3" />
                                  {investor.phone}
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-4 text-right text-sm font-medium whitespace-nowrap">
                              {formatPlatformCurrency(investor.investedAmountNum)}
                            </td>
                            <td className="py-4 px-4 text-right whitespace-nowrap">
                              <p className="text-sm font-medium">{formatPlatformCurrency(investor.currentValueNum)}</p>
                              {investor.investedAmountNum > 0 && (
                                <p className="text-xs text-green-600 dark:text-green-400">
                                  +{(((investor.currentValueNum - investor.investedAmountNum) / investor.investedAmountNum) * 100).toFixed(1)}%
                                </p>
                              )}
                            </td>
                            <td className="py-4 px-4 text-center">
                              <Badge
                                variant="outline"
                                className={
                                  investor.kycStatus === "verified"
                                    ? "bg-green-500/10 text-green-600 border-green-500/20"
                                    : "bg-yellow-500/10 text-yellow-600 border-yellow-500/20"
                                }
                              >
                                {investor.kycStatus === "verified" ? "Verified" : "Pending"}
                              </Badge>
                            </td>
                            <td className="py-4 px-4 text-right pr-4" onClick={(e) => e.stopPropagation()}>
                              <div className="flex justify-end items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-accent"
                                  onClick={() => handleEditProfile(investor)}
                                >
                                  <Edit className="h-4 h-4" />
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleViewDetails(investor)}>
                                      <Eye className="w-4 h-4 mr-2" />
                                      View Details
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleGenerateStatement(investor)}>
                                      <FileText className="w-4 h-4 mr-2" />
                                      Generate Statement
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleViewTransactions(investor)}>
                                      <History className="w-4 h-4 mr-2" />
                                      Transactions
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        const inv = resolveBackendInvestor(investor);
                                        setSelectedInvestor(inv);
                                        setShowAgreementsDialog(true);
                                      }}
                                    >
                                      <Upload className="w-4 h-4 mr-2" />
                                      Agreements / Deleted Contracts
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => sendCredentialsMutation.mutate(investor.id)}
                                    >
                                      <Send className="w-4 h-4 mr-2" />
                                      Send Credentials
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      className="text-destructive focus:text-destructive"
                                      onClick={() => handleDeleteWithConfirmation(investor)}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </td>
                          </tr>

                          {/* Expanded Section */}
                          {expandedInvestorId === investor.id && (
                            <tr className="bg-muted/10">
                              <td colSpan={8} className="p-6">
                                <div className="space-y-8 animate-in slide-in-from-top-2 duration-300">
                                  <div className="flex justify-between items-center mb-2">
                                    <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Detailed Portfolio Records</h3>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="gap-2 text-xs"
                                      onClick={() => setExpandedInvestorId(null)}
                                    >
                                      <X className="w-3 h-3" />
                                      Collapse
                                    </Button>
                                  </div>

                                  {/* List A: Investments */}
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 rounded-full bg-accent" />
                                      <h4 className="font-bold text-base">Investment List A</h4>
                                    </div>
                                    <div className="rounded-lg border overflow-hidden bg-background shadow-sm">
                                      <table className="w-full text-[13px]">
                                        <thead className="bg-accent/5 border-b">
                                          <tr>
                                            <th className="text-left py-2 px-4 font-semibold">Date</th>
                                            <th className="text-left py-2 px-4 font-semibold">Product</th>
                                            <th className="text-right py-2 px-4 font-semibold">Amount (INR)</th>
                                            <th className="text-left py-2 px-4 font-semibold">Payout (INR)</th>
                                            <th className="text-center py-2 px-4 font-semibold">Lock-in</th>
                                            <th className="text-center py-2 px-4 font-semibold">Maturity</th>
                                            <th className="text-right py-2 px-4 font-semibold">Returns</th>
                                            <th className="text-right py-2 px-4 font-semibold">Total Portfolio</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {allPortfolios?.filter(p => p.investorId === investor.id).map(p => (
                                            <tr key={p._id} className="border-b last:border-0 hover:bg-muted/30">
                                              <td className="py-2.5 px-4 font-medium">{new Date(p.inceptionDate).toLocaleDateString("en-IN")}</td>
                                              <td className="py-2.5 px-4 uppercase text-[10px] tracking-tighter">{p.fundName}</td>
                                              <td className="py-2.5 px-4 text-right font-mono font-bold text-accent">{formatPlatformCurrencyExact(parseInt(p.totalInvested))}</td>
                                              <td className="py-2.5 px-4 text-[11px] text-muted-foreground">{p.payout || "Lum Sum at end"}</td>
                                              <td className="py-2.5 px-4 text-center">{p.lockIn || "-"}</td>
                                              <td className="py-2.5 px-4 text-center">{p.maturity || "-"}</td>
                                              <td className="py-2.5 px-4 text-right text-green-600 font-medium">{formatPlatformCurrencyExact(parseInt(p.returns))}</td>
                                              <td className="py-2.5 px-4 text-right font-bold text-accent">{formatPlatformCurrencyExact(parseInt(p.currentValue))}</td>
                                            </tr>
                                          ))}
                                          {(!allPortfolios || allPortfolios.filter(p => p.investorId === investor.id).length === 0) && (
                                            <tr><td colSpan={8} className="py-4 text-center text-muted-foreground italic">No active investments found</td></tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>

                                  {/* List B: Withdrawals */}
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 rounded-full bg-red-500" />
                                      <h4 className="font-bold text-base">Withdrawal B</h4>
                                    </div>
                                    <div className="rounded-lg border overflow-hidden bg-background shadow-sm">
                                      <table className="w-full text-[13px]">
                                        <thead className="bg-red-500/5 border-b">
                                          <tr>
                                            <th className="text-left py-2 px-4 font-semibold">Date of Investment</th>
                                            <th className="text-left py-2 px-4 font-semibold text-red-600">Withdraw Date</th>
                                            <th className="text-left py-2 px-4 font-semibold">Product</th>
                                            <th className="text-right py-2 px-4 font-semibold">Amount (INR)</th>
                                            <th className="text-right py-2 px-4 font-semibold">Payout (INR)</th>
                                            <th className="text-right py-2 px-4 font-semibold">IRR%</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {allWithdrawals?.filter(w => w.investorId === investor.id).map(w => (
                                            <tr key={w._id} className="border-b last:border-0 hover:bg-muted/30">
                                              <td className="py-2.5 px-4 font-medium">{new Date(w.investmentDate).toLocaleDateString("en-IN")}</td>
                                              <td className="py-2.5 px-4 text-red-500 font-bold">{new Date(w.withdrawalDate).toLocaleDateString("en-IN")}</td>
                                              <td className="py-2.5 px-4 uppercase text-[10px] tracking-tighter">
                                                {allPortfolios?.find(p => p._id === w.portfolioId)?.fundName || "Fund"}
                                              </td>
                                              <td className="py-2.5 px-4 text-right font-mono font-bold">{formatPlatformCurrencyExact(parseInt(w.amount))}</td>
                                              <td className="py-2.5 px-4 text-right font-semibold text-accent">{formatPlatformCurrencyExact(parseInt(w.payout))}</td>
                                              <td className="py-2.5 px-4 text-right text-muted-foreground">{w.irr}%</td>
                                            </tr>
                                          ))}
                                          {(!allWithdrawals || allWithdrawals.filter(w => w.investorId === investor.id).length === 0) && (
                                            <tr><td colSpan={6} className="py-4 text-center text-muted-foreground italic">No withdrawal records found</td></tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </main>
        </div>
      </div>

      <Dialog open={showAddDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{onboardingComplete ? "Investor Onboarded" : "Onboard New Investor"}</DialogTitle>
            <DialogDescription>
              {onboardingComplete
                ? "The investor account has been created successfully."
                : "Add a new investor to the Velocity Fund platform."}
            </DialogDescription>
          </DialogHeader>

          {onboardingComplete && generatedCredentials ? (
            <div className="space-y-6">
              <div className="p-6 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h4 className="font-medium">Account Created</h4>
                    <p className="text-sm text-muted-foreground">Share these credentials with the investor</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="p-3 rounded-md bg-background">
                    <p className="text-xs text-muted-foreground mb-1">Login Email</p>
                    <p className="font-mono text-sm">{generatedCredentials.email}</p>
                  </div>
                  <div className="p-3 rounded-md bg-background">
                    <p className="text-xs text-muted-foreground mb-1">Temporary Password</p>
                    <p className="font-mono text-sm">{generatedCredentials.tempPassword}</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={handleCloseDialog}>
                  Close
                </Button>
                <Button className="flex-1" onClick={() => {
                  navigator.clipboard.writeText(`Login: ${generatedCredentials.email}\nPassword: ${generatedCredentials.tempPassword}`);
                  toast({ title: "Copied", description: "Credentials copied to clipboard" });
                }}>
                  Copy Credentials
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    placeholder="Rahul"
                    {...form.register("firstName")}
                    data-testid="admin-input-investor-firstname"
                  />
                  {form.formState.errors.firstName && (
                    <p className="text-sm text-destructive">{form.formState.errors.firstName.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    placeholder="Sharma"
                    {...form.register("lastName")}
                    data-testid="admin-input-investor-lastname"
                  />
                  {form.formState.errors.lastName && (
                    <p className="text-sm text-destructive">{form.formState.errors.lastName.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="rahul.sharma@email.com"
                  {...form.register("email")}
                  data-testid="admin-input-investor-email"
                />
                {form.formState.errors.email && (
                  <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    placeholder="+91 98765 43210"
                    {...form.register("phone")}
                    data-testid="admin-input-investor-phone"
                  />
                  {form.formState.errors.phone && (
                    <p className="text-sm text-destructive">{form.formState.errors.phone.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="panNumber">PAN Number</Label>
                  <Input
                    id="panNumber"
                    placeholder="ABCDE1234F"
                    {...form.register("panNumber")}
                    className="uppercase"
                    data-testid="admin-input-investor-pan"
                  />
                  {form.formState.errors.panNumber && (
                    <p className="text-sm text-destructive">{form.formState.errors.panNumber.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="clientId">Investor ID (Optional)</Label>
                  <Input
                    id="clientId"
                    placeholder="GC2026071"
                    {...form.register("clientId")}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="investorType">Investor Type</Label>
                  <Select onValueChange={(value) => form.setValue("investorType", value)}>
                    <SelectTrigger data-testid="admin-select-investor-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual (₹1 Lac – ₹50 Lacs)</SelectItem>
                      <SelectItem value="hni">HNI (₹1 Cr – ₹5 Cr)</SelectItem>
                      <SelectItem value="uhni">UHNI (₹5 Cr – ₹25 Cr)</SelectItem>
                      <SelectItem value="institutional">Institutional (₹25 Cr+)</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.formState.errors.investorType && (
                    <p className="text-sm text-destructive">{form.formState.errors.investorType.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="investmentAmount">Investment Amount (INR)</Label>
                <Input
                  id="investmentAmount"
                  type="number"
                  placeholder="10000000"
                  {...form.register("investmentAmount")}
                  data-testid="admin-input-investor-amount"
                />
                {form.formState.errors.investmentAmount && (
                  <p className="text-sm text-destructive">{form.formState.errors.investmentAmount.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter password"
                    {...form.register("password")}
                    data-testid="admin-input-investor-password"
                  />
                  {form.formState.errors.password && (
                    <p className="text-sm text-destructive">{form.formState.errors.password.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm password"
                    {...form.register("confirmPassword")}
                    data-testid="admin-input-investor-confirm-password"
                  />
                  {form.formState.errors.confirmPassword && (
                    <p className="text-sm text-destructive">{form.formState.errors.confirmPassword.message}</p>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={handleCloseDialog}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" disabled={mutation.isPending} data-testid="admin-button-submit-investor">
                  {mutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Investor Account"
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the investor account for
              <span className="font-medium text-foreground"> {investorToDelete?.firstName} {investorToDelete?.lastName} </span>
              and remove all associated data including portfolio and transactions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteInvestorMutation.isPending}
            >
              {deleteInvestorMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Details Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Investor Details</DialogTitle>
            <DialogDescription>View complete investor profile information</DialogDescription>
          </DialogHeader>
          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : investorDetails ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Name</p>
                  <p className="font-medium">{investorDetails.firstName} {investorDetails.lastName}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Investor ID</p>
                  <p className="font-medium font-mono">{investorDetails.clientId || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="font-medium">{investorDetails.email || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="font-medium">{investorDetails.phone || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">KYC Status</p>
                  <Badge className={investorDetails.kycStatus === "verified" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                    {investorDetails.kycStatus || "pending"}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Invested</p>
                  <p className="font-medium">{formatPlatformCurrency(investorDetails.portfolio?.totalInvested || 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Current Value</p>
                  <p className="font-medium">{formatPlatformCurrency(investorDetails.portfolio?.currentValue || 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Net Returns</p>
                  <p className="font-medium">{formatPlatformCurrency(investorDetails.portfolio?.returns || 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Net IRR</p>
                  <p className="font-medium">{investorDetails.portfolio?.irr || 0}%</p>
                </div>
              </div>
              {investorDetails.portfolio && (
                <div className="rounded-lg border p-3 bg-muted/30">
                  <p className="text-xs text-muted-foreground mb-2">
                    {investorDetails.portfolio.fundName || "Velocity Fund"} · Portfolio Allocation
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground">Private Credit</p>
                      <p className="font-medium">{investorDetails.portfolio.privateCreditAllocation || 0}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">AIF Exposure</p>
                      <p className="font-medium">{investorDetails.portfolio.aifExposure || 0}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Cash</p>
                      <p className="font-medium">{investorDetails.portfolio.cashEquivalents || 0}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Currency/Commodities</p>
                      <p className="font-medium">{investorDetails.portfolio.currencyCommodities || 0}%</p>
                    </div>
                  </div>
                </div>
              )}
              <Button className="w-full" onClick={() => setShowViewDialog(false)}>Close</Button>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">No details available</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Investor Profile & Portfolio</DialogTitle>
            <DialogDescription>Update investor information, portfolio holdings, and asset allocations</DialogDescription>
          </DialogHeader>
          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : investorDetails ? (
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const portfolioId =
                investorDetails.portfolio?._id || investorDetails.portfolio?.id || undefined;

              const privateCreditAllocation = Number(formData.get("privateCreditAllocation") || 0);
              const aifExposure = Number(formData.get("aifExposure") || 0);
              const cashEquivalents = Number(formData.get("cashEquivalents") || 0);
              const currencyCommodities = Number(formData.get("currencyCommodities") || 0);
              const allocationTotal = privateCreditAllocation + aifExposure + cashEquivalents + currencyCommodities;

              if (portfolioId && Math.abs(allocationTotal - 100) > 0.01) {
                toast({
                  title: "Invalid allocation",
                  description: "Private Credit + AIF Exposure + Cash + Currency/Commodities must total 100%.",
                  variant: "destructive",
                });
                return;
              }

              editInvestorMutation.mutate({
                id: selectedInvestor?.id || "",
                updates: {
                  firstName: formData.get("firstName"),
                  lastName: formData.get("lastName"),
                  clientId: formData.get("clientId"),
                  phone: formData.get("phone"),
                  investorType: formData.get("investorType"),
                  kycStatus: formData.get("kycStatus"),
                  password: formData.get("password") || undefined,
                },
                portfolioId,
                allocation: portfolioId
                  ? {
                      privateCreditAllocation,
                      aifExposure,
                      cashEquivalents,
                      currencyCommodities,
                      fundName: String(formData.get("fundName") || "Velocity Fund"),
                      totalInvested: String(formData.get("totalInvested") || "0"),
                      currentValue: String(formData.get("currentValue") || "0"),
                      returns: String(formData.get("returns") || "0"),
                      irr: String(formData.get("irr") || "0"),
                      maturity: String(formData.get("maturity") || "36 Months"),
                      allocationDate: String(formData.get("allocationDate") || ""),
                      deploymentStatus: String(formData.get("deploymentStatus") || "active"),
                    }
                  : undefined,
              });
            }} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editFirstName">First Name</Label>
                  <Input id="editFirstName" name="firstName" defaultValue={investorDetails.firstName || ""} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editLastName">Last Name</Label>
                  <Input id="editLastName" name="lastName" defaultValue={investorDetails.lastName || ""} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editClientId">Investor ID</Label>
                  <Input id="editClientId" name="clientId" defaultValue={investorDetails.clientId || ""} placeholder="e.g. GC2026071" className="font-mono" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editInvestorType">Investor Type</Label>
                  <select
                    id="editInvestorType"
                    name="investorType"
                    defaultValue={investorDetails.investorType || "individual"}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="individual">Individual (₹1 Lac – ₹50 Lacs)</option>
                    <option value="hni">HNI (₹1 Cr – ₹5 Cr)</option>
                    <option value="uhni">UHNI (₹5 Cr – ₹25 Cr)</option>
                    <option value="institutional">Institutional (₹25 Cr+)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editPhone">Phone</Label>
                  <Input id="editPhone" name="phone" defaultValue={investorDetails.phone || ""} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editKycStatus">KYC Status</Label>
                  <select
                    id="editKycStatus"
                    name="kycStatus"
                    defaultValue={investorDetails.kycStatus || "pending"}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="verified">Verified</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="editPassword">New Password (Optional)</Label>
                <Input id="editPassword" name="password" type="password" placeholder="Enter new password to update" />
              </div>

              {investorDetails.portfolio ? (
                <div className="space-y-4 rounded-lg border p-4 bg-muted/20">
                  <h4 className="font-semibold text-sm border-b pb-2">Portfolio Investment & Holdings Override</h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fundName">Product Name</Label>
                      <select
                        id="fundName"
                        name="fundName"
                        defaultValue={investorDetails.portfolio.fundName || "Velocity Fund"}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="Velocity Fund">Velocity Fund</option>
                        <option value="Velocity Fund Fx">Velocity Fund Fx</option>
                        <option value="Neo Edge Plus">Neo Edge Plus</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="deploymentStatus">Status</Label>
                      <select
                        id="deploymentStatus"
                        name="deploymentStatus"
                        defaultValue={investorDetails.portfolio.deploymentStatus || "active"}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="deployed">Deployed</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="totalInvested">Total Invested (INR)</Label>
                      <Input
                        id="totalInvested"
                        name="totalInvested"
                        type="number"
                        defaultValue={investorDetails.portfolio.totalInvested || "0"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="currentValue">Current Value (INR)</Label>
                      <Input
                        id="currentValue"
                        name="currentValue"
                        type="number"
                        defaultValue={investorDetails.portfolio.currentValue || "0"}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="returns">Net Returns / Till Date (INR)</Label>
                      <Input
                        id="returns"
                        name="returns"
                        type="number"
                        defaultValue={investorDetails.portfolio.returns || "0"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="irr">Net IRR (%)</Label>
                      <Input
                        id="irr"
                        name="irr"
                        type="number"
                        step="0.01"
                        defaultValue={investorDetails.portfolio.irr || "0"}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="maturity">Tenure / Maturity</Label>
                      <Input
                        id="maturity"
                        name="maturity"
                        defaultValue={investorDetails.portfolio.maturity || "36 Months"}
                        placeholder="e.g. 36 Months"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="allocationDate">Allocation Date</Label>
                      <Input
                        id="allocationDate"
                        name="allocationDate"
                        type="date"
                        defaultValue={
                          investorDetails.portfolio.allocationDate
                            ? new Date(investorDetails.portfolio.allocationDate).toISOString().split("T")[0]
                            : ""
                        }
                      />
                    </div>
                  </div>

                  <div className="pt-2">
                    <p className="text-sm font-medium">Portfolio Allocation Breakdown (%)</p>
                    <p className="text-xs text-muted-foreground mb-3">
                      Percentages shown on dashboard pie chart. Must total 100%.
                    </p>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="privateCreditAllocation">Private Credit (%)</Label>
                        <Input
                          id="privateCreditAllocation"
                          name="privateCreditAllocation"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          defaultValue={investorDetails.portfolio.privateCreditAllocation || "0"}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="aifExposure">AIF Exposure (%)</Label>
                        <Input
                          id="aifExposure"
                          name="aifExposure"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          defaultValue={investorDetails.portfolio.aifExposure || "0"}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cashEquivalents">Cash (%)</Label>
                        <Input
                          id="cashEquivalents"
                          name="cashEquivalents"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          defaultValue={investorDetails.portfolio.cashEquivalents || "0"}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="currencyCommodities">Currency/Commodities (%)</Label>
                        <Input
                          id="currencyCommodities"
                          name="currencyCommodities"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          defaultValue={investorDetails.portfolio.currencyCommodities || "0"}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground rounded-lg border p-3 bg-muted/20">
                  No portfolio found for this investor.
                </p>
              )}

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowEditDialog(false)}>Cancel</Button>
                <Button type="submit" className="flex-1" disabled={editInvestorMutation.isPending}>
                  {editInvestorMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : "Save Changes"}
                </Button>
              </div>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Upload/View Agreements Dialog */}
      <Dialog open={showAgreementsDialog} onOpenChange={setShowAgreementsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Client Agreements & Deleted Contracts</DialogTitle>
            <DialogDescription>
              Manage signed PDF agreements for {selectedInvestor?.firstName} {selectedInvestor?.lastName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-3 rounded-lg border p-4 bg-muted/20">
              <h4 className="font-semibold text-sm">Upload New Agreement</h4>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="agreementTitle">Agreement Title</Label>
                  <Input
                    id="agreementTitle"
                    value={agreementTitle}
                    onChange={(e) => setAgreementTitle(e.target.value)}
                    placeholder="e.g. Contribution & Investment Agreement"
                  />
                </div>
                <div>
                  <Label htmlFor="agreementFile">Select PDF File</Label>
                  <Input
                    id="agreementFile"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setAgreementFile(e.target.files?.[0] || null)}
                  />
                </div>
                <Button
                  className="w-full gap-2"
                  disabled={!agreementFile || uploadAgreementMutation.isPending}
                  onClick={() => uploadAgreementMutation.mutate()}
                >
                  {uploadAgreementMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Upload Agreement PDF
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-sm">Uploaded Agreements</h4>
              {loadingAgreements ? (
                <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" /></div>
              ) : investorAgreements && investorAgreements.length > 0 ? (
                <div className="space-y-2">
                  {investorAgreements.map((ag: any) => (
                    <div key={ag._id || ag.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-accent" />
                        <div>
                          <p className="font-medium text-sm">{ag.title}</p>
                          <p className="text-xs text-muted-foreground">{ag.fileName} • {new Date(ag.createdAt).toLocaleDateString("en-IN")}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={`${API_BASE_URL}/api/agreements/${ag._id || ag.id}/download`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Button size="sm" variant="outline">Download</Button>
                        </a>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => deleteAgreementMutation.mutate(ag._id || ag.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic py-2">No agreements uploaded yet.</p>
              )}
            </div>

            <Button variant="outline" className="w-full" onClick={() => setShowAgreementsDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generate Statement Dialog */}
      <Dialog open={showGenerateStatementDialog} onOpenChange={setShowGenerateStatementDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Statement</DialogTitle>
            <DialogDescription>Create a new statement for {selectedInvestor?.firstName} {selectedInvestor?.lastName}</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            const periodRaw = String(formData.get("period") || "July_2026");
            const [periodLabel, yearStr] = periodRaw.split("_");
            const year = parseInt(yearStr || String(new Date().getFullYear()), 10);
            generateStatementMutation.mutate({
              investorId: selectedInvestor?.id || "",
              type: String(formData.get("type") || "monthly"),
              period: periodLabel,
              year: year,
            });
          }} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="statementType">Statement Type</Label>
              <select
                id="statementType"
                name="type"
                defaultValue="monthly"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="monthly">Monthly Statement</option>
                <option value="quarterly">Quarterly Statement</option>
                <option value="annual">Annual Statement</option>
                <option value="capital_account">Capital Account Statement</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="statementPeriod">Period</Label>
              <select
                id="statementPeriod"
                name="period"
                defaultValue="July_2026"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="Jan_2026">Jan 2026</option>
                <option value="Feb_2026">Feb 2026</option>
                <option value="Mar_2026">Mar 2026</option>
                <option value="Apr_2026">Apr 2026</option>
                <option value="May_2026">May 2026</option>
                <option value="Jun_2026">Jun 2026</option>
                <option value="July_2026">July 2026</option>
                <option value="Aug_2026">Aug 2026</option>
                <option value="Sep_2026">Sep 2026</option>
                <option value="Oct_2026">Oct 2026</option>
                <option value="Nov_2026">Nov 2026</option>
                <option value="Dec_2026">Dec 2026</option>
                <option value="Q1_2026">Q1 2026</option>
                <option value="Q2_2026">Q2 2026</option>
                <option value="Q3_2026">Q3 2026</option>
                <option value="Q4_2026">Q4 2026</option>
                <option value="FY_2026">FY 2026</option>
              </select>
            </div>
            <Button type="submit" className="w-full" disabled={generateStatementMutation.isPending}>
              {generateStatementMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : "Generate Statement"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Transactions Dialog */}
      <Dialog open={showTransactionsDialog} onOpenChange={setShowTransactionsDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Transaction History</DialogTitle>
            <DialogDescription>
              View and manage transactions for {selectedInvestor?.firstName} {selectedInvestor?.lastName}
            </DialogDescription>
          </DialogHeader>
          {loadingTransactions ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : investorTransactions && investorTransactions.length > 0 ? (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium text-muted-foreground">Date</th>
                    <th className="text-left py-2 font-medium text-muted-foreground">Type</th>
                    <th className="text-right py-2 font-medium text-muted-foreground">Amount</th>
                    <th className="text-center py-2 font-medium text-muted-foreground">Status</th>
                    <th className="text-right py-2 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {investorTransactions.map((txn: { _id?: string; id?: string; createdAt: string; type: string; amount: string; status: string }) => {
                    const txnId = txn._id || txn.id || "";
                    return (
                      <tr key={txnId} className="border-b last:border-0">
                        <td className="py-3">{new Date(txn.createdAt).toLocaleDateString("en-IN")}</td>
                        <td className="py-3 capitalize">{txn.type}</td>
                        <td className="py-3 text-right font-medium">
                          {formatPlatformCurrencyExact(parseFloat(txn.amount))}
                        </td>
                        <td className="py-3 text-center">
                          <Badge variant="outline" className="capitalize">{txn.status}</Badge>
                        </td>
                        <td className="py-3 text-right">
                          <div className="flex justify-end gap-1">
                            {txn.status !== "verified" && txn.status !== "processed" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() => verifyTransactionMutation.mutate(txnId)}
                              >
                                Verify
                              </Button>
                            )}
                            {txn.status === "verified" && (
                              <Button
                                size="sm"
                                className="h-7 text-xs gap-1"
                                onClick={() => processTransactionMutation.mutate(txnId)}
                              >
                                <Play className="w-3 h-3" />
                                Process
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No transactions found</p>
          )}
          <Button className="w-full" onClick={() => setShowTransactionsDialog(false)}>Close</Button>
        </DialogContent>
      </Dialog>

      {/* Bulk upload */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk upload investors</DialogTitle>
            <DialogDescription>
              Upload a CSV with columns: firstName, lastName, email, phone, panNumber, investmentAmount, investorType
            </DialogDescription>
          </DialogHeader>
          <Input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
          />
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => { setShowBulkDialog(false); setBulkFile(null); }}>
              Cancel
            </Button>
            <Button className="flex-1" disabled={!bulkFile || bulkUploadMutation.isPending} onClick={handleBulkUpload}>
              {bulkUploadMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : "Upload CSV"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generated credentials */}
      <Dialog open={!!credentialsPreview} onOpenChange={() => setCredentialsPreview(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Investor credentials</DialogTitle>
            <DialogDescription>Share securely with the investor</DialogDescription>
          </DialogHeader>
          {credentialsPreview && (
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-muted">
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="font-mono text-sm">{credentialsPreview.email}</p>
              </div>
              <div className="p-3 rounded-md bg-muted">
                <p className="text-xs text-muted-foreground">Temporary password</p>
                <p className="font-mono text-sm">{credentialsPreview.tempPassword}</p>
              </div>
              <Button
                className="w-full"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `Login: ${credentialsPreview.email}\nPassword: ${credentialsPreview.tempPassword}`
                  );
                  toast({ title: "Copied to clipboard" });
                }}
              >
                Copy credentials
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
}
